import { useState, useEffect, useCallback } from 'react';
import { getCtTracker, ctCarryForward, ctBulkInitialize, getCtReminders, getCtReminderSettings, updateCtReminderSettings } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  CheckCircle, Clock, AlertTriangle, PlayCircle, Users, MapPin,
  Settings, Loader2, ChevronDown, ChevronUp, Edit3, Lock, Unlock,
  ShieldAlert, Zap, ArrowRight
} from 'lucide-react';

const STATUS_CONFIG = {
  initialized: { label: 'Initialized', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200', icon: CheckCircle },
  edited: { label: 'Edited', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200', icon: Edit3 },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200', icon: Clock },
};

export function CtStatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${cfg.color}`} data-testid={`ct-status-${status}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

function getBarColor(pct) {
  if (pct >= 100) return 'text-emerald-600';
  if (pct >= 50) return 'text-amber-600';
  return 'text-rose-600';
}

function getBarBg(pct) {
  if (pct >= 100) return 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/80 dark:bg-emerald-950/20';
  if (pct >= 50) return 'border-amber-200 dark:border-amber-800 bg-amber-50/80 dark:bg-amber-950/20';
  return 'border-rose-200 dark:border-rose-800 bg-rose-50/80 dark:bg-rose-950/20';
}

export function CtInitPanel({ month, state, district, onInitComplete, compact }) {
  const [tracker, setTracker] = useState(null);
  const [reminder, setReminder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [districtBulkLoading, setDistrictBulkLoading] = useState(null);
  const [singleLoading, setSingleLoading] = useState(null);
  const [expandedDistrict, setExpandedDistrict] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [deadlineDay, setDeadlineDay] = useState(5);

  const load = useCallback(async () => {
    if (!month || !state || state === 'all') return;
    setLoading(true);
    try {
      const [tRes, rRes, sRes] = await Promise.all([
        getCtTracker(month, state, district),
        getCtReminders(month, state),
        getCtReminderSettings()
      ]);
      setTracker(tRes.data);
      setReminder(rRes.data);
      setDeadlineDay(sRes.data.deadline_day);
    } catch { toast.error('Failed to load CT tracker'); }
    finally { setLoading(false); }
  }, [month, state, district]);

  useEffect(() => { load(); }, [load]);

  const handleBulkInit = async () => {
    setBulkLoading(true);
    try {
      const res = await ctBulkInitialize(month, state, district);
      toast.success(`Initialized ${res.data.initialized} partners. ${res.data.skipped_already_done} already done.`);
      await load();
      onInitComplete?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk init failed'); }
    finally { setBulkLoading(false); }
  };

  const handleDistrictInit = async (districtName) => {
    setDistrictBulkLoading(districtName);
    try {
      const res = await ctBulkInitialize(month, state, districtName);
      toast.success(`${districtName}: Initialized ${res.data.initialized} partners`);
      await load();
      onInitComplete?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Init failed'); }
    finally { setDistrictBulkLoading(null); }
  };

  const handleSingleInit = async (aptekaId) => {
    setSingleLoading(aptekaId);
    try {
      const res = await ctCarryForward(aptekaId, month);
      toast.success(`Initialized ${res.data.total} products from ${res.data.from_month}`);
      await load();
      onInitComplete?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Init failed'); }
    finally { setSingleLoading(null); }
  };

  const handleSaveSettings = async () => {
    try {
      await updateCtReminderSettings(deadlineDay);
      toast.success('Reminder settings saved');
      setShowSettings(false);
      await load();
    } catch { toast.error('Failed to save'); }
  };

  if (loading && !tracker) {
    return (
      <div className="rounded-xl border bg-card p-4" data-testid="ct-init-panel-loading">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading CT tracker...</span>
        </div>
      </div>
    );
  }

  if (!tracker || tracker.total === 0) return null;

  const isComplete = tracker.pending === 0;
  const isOverdue = reminder?.is_overdue && !isComplete;
  const hasAlerts = tracker.alerts?.length > 0;
  const districtPartners = expandedDistrict
    ? (tracker.partners || []).filter(p => p.district === expandedDistrict)
    : [];

  return (
    <div className={`rounded-xl border-2 overflow-hidden transition-all ${isComplete ? 'border-emerald-300 dark:border-emerald-800' : isOverdue ? 'border-rose-300 dark:border-rose-700' : 'border-amber-300 dark:border-amber-700'}`} data-testid="ct-init-panel">

      {/* ===== HEADER WITH GLOBAL SUMMARY ===== */}
      <div className={`px-4 py-3 ${isComplete ? 'bg-emerald-50 dark:bg-emerald-950/20' : isOverdue ? 'bg-rose-50 dark:bg-rose-950/20' : 'bg-amber-50 dark:bg-amber-950/20'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isComplete ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : isOverdue ? <ShieldAlert className="w-5 h-5 text-rose-600 animate-pulse" /> : <Clock className="w-5 h-5 text-amber-600" />}
            <div>
              <h3 className="text-sm font-bold flex items-center gap-2" data-testid="ct-init-title">
                Stock Initialization — {month}
                {isComplete && <Badge className="bg-emerald-600 text-white text-[9px] h-4">Complete</Badge>}
                {isOverdue && <Badge variant="destructive" className="text-[9px] h-4 animate-pulse">Overdue</Badge>}
                {!isComplete && !isOverdue && <Badge className="bg-amber-500 text-white text-[9px] h-4">In Progress</Badge>}
              </h3>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {tracker.initialized}/{tracker.total} partners initialized
                {tracker.edited > 0 && <span className="ml-1 text-blue-600"> | {tracker.edited} edited</span>}
                {tracker.pending > 0 && <span className="ml-1 font-bold text-amber-700 dark:text-amber-400"> | {tracker.pending} pending</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setShowSettings(true)} data-testid="ct-settings-btn">
              <Settings className="w-3.5 h-3.5" />
            </Button>
            {!isComplete && (
              <Button size="sm" className="h-7 text-xs gap-1.5 font-bold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white border-0 shadow-sm"
                onClick={handleBulkInit} disabled={bulkLoading} data-testid="ct-bulk-init-btn">
                {bulkLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                Initialize All ({tracker.pending})
              </Button>
            )}
          </div>
        </div>

        {/* Global Progress Bar */}
        <div className="mt-2.5 flex items-center gap-3">
          <div className="flex-1">
            <Progress value={tracker.pct} className="h-2.5" />
          </div>
          <span className={`text-sm font-bold tabular-nums ${getBarColor(tracker.pct)}`} data-testid="ct-global-pct">{tracker.pct}%</span>
        </div>
      </div>

      {/* ===== OVERDUE ALERT BANNER ===== */}
      {isOverdue && (
        <div className="px-4 py-2.5 bg-rose-100/80 dark:bg-rose-950/30 border-b border-rose-300 dark:border-rose-800 flex items-start gap-2" data-testid="ct-overdue-warning">
          <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-rose-800 dark:text-rose-300">CT initialization is overdue!</p>
            <p className="text-[10px] text-rose-700 dark:text-rose-400 mt-0.5">
              Deadline was day {reminder?.deadline_day} of the month. {tracker.pending} partner(s) still pending.
              Deliveries and payments are blocked until CT is initialized.
            </p>
          </div>
        </div>
      )}

      {/* ===== PENDING ALERTS (non-overdue) ===== */}
      {hasAlerts && !isOverdue && !isComplete && (
        <div className="px-4 py-2 bg-amber-50/80 dark:bg-amber-950/10 border-b border-amber-200 dark:border-amber-800 flex items-center gap-2" data-testid="ct-pending-alert">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
          <p className="text-[10px] text-amber-800 dark:text-amber-300">
            <span className="font-bold">{tracker.pending} partner(s)</span> across <span className="font-bold">{tracker.alerts.length} district(s)</span> still need CT initialization.
            {reminder && !reminder.is_overdue && reminder.days_remaining > 0 && (
              <span className="ml-1"> {reminder.days_remaining} day(s) remaining until deadline.</span>
            )}
          </p>
        </div>
      )}

      {/* ===== DISTRICT GRID ===== */}
      <div className="px-4 py-3 bg-card" data-testid="ct-district-section">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> District Completion Tracker
          </span>
          <span className="text-[10px] text-muted-foreground">{tracker.districts?.length} districts</span>
        </div>

        <div className="space-y-1.5" data-testid="ct-district-list">
          {tracker.districts?.map(d => {
            const isExpanded = expandedDistrict === d.district;
            const pendingPartners = isExpanded ? districtPartners : [];
            const colorClass = getBarBg(d.pct);

            return (
              <div key={d.district} className="rounded-lg border overflow-hidden" data-testid={`ct-district-${d.district}`}>
                {/* District Row */}
                <button
                  onClick={() => setExpandedDistrict(isExpanded ? null : d.district)}
                  className={`w-full px-3 py-2.5 flex items-center gap-3 text-left transition-colors hover:bg-muted/30 ${colorClass}`}
                  data-testid={`ct-district-row-${d.district}`}
                >
                  {/* Lock icon */}
                  <div className="shrink-0 w-5" data-testid={`ct-lock-${d.district}`}>
                    {d.locked ? <Lock className="w-4 h-4 text-rose-500" /> : <Unlock className="w-4 h-4 text-muted-foreground/40" />}
                  </div>

                  {/* District name */}
                  <div className="min-w-[120px] shrink-0">
                    <span className="text-xs font-bold">{d.district}</span>
                    {d.locked && <Badge variant="destructive" className="text-[8px] h-3.5 ml-1.5">Locked</Badge>}
                  </div>

                  {/* Progress bar */}
                  <div className="flex-1 min-w-[80px]">
                    <Progress value={d.pct} className="h-2" />
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right min-w-[50px]">
                      <span className={`text-xs font-bold tabular-nums ${getBarColor(d.pct)}`}>{d.pct}%</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] min-w-[90px]">
                      <span className="text-emerald-600 font-semibold tabular-nums">{d.initialized}</span>
                      <span className="text-muted-foreground">/</span>
                      <span className="font-semibold tabular-nums">{d.total}</span>
                      {d.pending > 0 && (
                        <Badge variant="outline" className="text-[8px] h-4 border-amber-300 text-amber-700 px-1">{d.pending} left</Badge>
                      )}
                    </div>
                    <div className="shrink-0">
                      {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
                    </div>
                  </div>
                </button>

                {/* Expanded: Partner List for this District */}
                {isExpanded && (
                  <div className="border-t bg-card" data-testid={`ct-district-expanded-${d.district}`}>
                    {/* District quick action */}
                    {d.pending > 0 && (
                      <div className="px-3 py-2 bg-muted/30 border-b flex items-center justify-between">
                        <span className="text-[10px] text-muted-foreground">
                          <Users className="w-3 h-3 inline mr-1" />
                          {d.pending} pending partner(s) in {d.district}
                        </span>
                        <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 font-bold border-amber-300 text-amber-700 hover:bg-amber-50"
                          onClick={(e) => { e.stopPropagation(); handleDistrictInit(d.district); }}
                          disabled={districtBulkLoading === d.district}
                          data-testid={`ct-district-init-${d.district}`}>
                          {districtBulkLoading === d.district ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
                          Initialize All Pending
                        </Button>
                      </div>
                    )}

                    {/* Partner rows */}
                    <div className="max-h-48 overflow-y-auto divide-y">
                      {pendingPartners.map(p => (
                        <div key={p.apteka_id} className="flex items-center justify-between px-3 py-2 hover:bg-muted/20" data-testid={`ct-partner-${p.apteka_id}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <CtStatusBadge status={p.status} />
                            <span className="text-xs font-medium truncate">{p.name}</span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {p.products_carried > 0 && (
                              <span className="text-[10px] text-muted-foreground tabular-nums">{p.products_carried} prods</span>
                            )}
                            {p.initialized_at && (
                              <span className="text-[10px] text-muted-foreground">{new Date(p.initialized_at).toLocaleDateString()}</span>
                            )}
                            {(p.status === 'pending' || p.status === 'edited') && (
                              <Button size="sm" variant={p.status === 'edited' ? 'default' : 'outline'} className={`h-5 text-[9px] gap-0.5 px-1.5 ${p.status === 'edited' ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
                                onClick={(e) => { e.stopPropagation(); handleSingleInit(p.apteka_id); }}
                                disabled={singleLoading === p.apteka_id}
                                data-testid={`ct-init-${p.apteka_id}`}>
                                {singleLoading === p.apteka_id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <ArrowRight className="w-2.5 h-2.5" />}
                                {p.status === 'edited' ? 'Fix & Init' : 'Init'}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      {pendingPartners.length === 0 && (
                        <div className="text-center py-4 text-xs text-muted-foreground">No partners in this district</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== CT FILL PROGRESS ===== */}
      {tracker.ct_fill && tracker.ct_fill.total > 0 && (
        <div className="px-4 py-3 border-t bg-card" data-testid="ct-fill-section">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold flex items-center gap-1.5">
              {tracker.ct_fill.pct >= 100 ? <CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />}
              CT Count Progress
            </span>
            <span className={`text-xs font-bold tabular-nums ${getBarColor(tracker.ct_fill.pct)}`}>
              {tracker.ct_fill.filled}/{tracker.ct_fill.total} partners
            </span>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex-1">
              <Progress value={tracker.ct_fill.pct} className="h-2" />
            </div>
            <span className={`text-sm font-bold tabular-nums ${getBarColor(tracker.ct_fill.pct)}`} data-testid="ct-fill-pct">
              {tracker.ct_fill.pct}%
            </span>
          </div>
          {tracker.ct_fill.unfilled > 0 && (
            <p className="text-[10px] text-amber-700 dark:text-amber-400 font-medium">
              {tracker.ct_fill.unfilled} partner(s) have unfilled CT counts — save is blocked until all products have CT filled
            </p>
          )}
          {tracker.ct_fill.pct >= 100 && (
            <p className="text-[10px] text-emerald-700 dark:text-emerald-400 font-medium">
              All partners have completed CT counts — month can be closed
            </p>
          )}
          {/* Per-district CT fill breakdown */}
          {Object.keys(tracker.ct_fill.districts || {}).length > 1 && (
            <div className="mt-2 space-y-1">
              {Object.entries(tracker.ct_fill.districts).map(([dist, d]) => {
                const dPct = d.total > 0 ? Math.round(d.filled / d.total * 100) : 0;
                return (
                  <div key={dist} className="flex items-center gap-2 text-[10px]">
                    <span className="font-medium w-24 truncate">{dist}</span>
                    <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full rounded-full ${dPct >= 100 ? 'bg-emerald-500' : dPct >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`}
                        style={{ width: `${dPct}%` }} />
                    </div>
                    <span className={`font-bold tabular-nums ${getBarColor(dPct)}`}>{d.filled}/{d.total}</span>
                    {d.unfilled_products > 0 && <span className="text-rose-500 tabular-nums">{d.unfilled_products} products left</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===== SETTINGS DIALOG ===== */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-sm" data-testid="ct-settings-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">CT Reminder Settings</DialogTitle>
            <DialogDescription className="text-xs">Set the day of the month by which CT must be initialized</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Deadline Day (of month)</label>
              <Input type="number" min={1} max={28} value={deadlineDay}
                onChange={e => setDeadlineDay(parseInt(e.target.value) || 5)}
                className="h-8 text-xs mt-1" data-testid="ct-deadline-input" />
              <p className="text-[10px] text-muted-foreground mt-1">
                Partners not initialized by day {deadlineDay} will show as overdue.
                Operations (deliveries, payments) are blocked until CT is initialized.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowSettings(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSaveSettings} data-testid="ct-save-settings-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
